import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { LogOut, Trash2, Inbox, Loader2, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { BRAND } from "@/data";

const STATUS_OPTS = [
  { value: "new", label: "Nuovo", cls: "bg-gold/20 text-gold border-gold/40" },
  { value: "contacted", label: "Contattato", cls: "bg-blue-500/20 text-blue-300 border-blue-500/40" },
  { value: "won", label: "Confermato", cls: "bg-green-500/20 text-green-300 border-green-500/40" },
  { value: "archived", label: "Archiviato", cls: "bg-stone-700/40 text-stone-400 border-stone-600" },
];

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLeads = async () => {
    try {
      const { data } = await api.get("/admin/leads");
      setLeads(data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user === false) {
      navigate("/admin");
      return;
    }
    if (user && user.email) fetchLeads();
  }, [user]);

  const changeStatus = async (id, status) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await api.patch(`/admin/leads/${id}?status=${status}`);
      toast.success("Stato aggiornato");
    } catch {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const removeLead = async (id) => {
    try {
      await api.delete(`/admin/leads/${id}`);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success("Richiesta eliminata");
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const doLogout = async () => {
    await logout();
    window.location.assign("/admin");
  };

  const badgeFor = (status) => {
    const opt = STATUS_OPTS.find((o) => o.value === status) || STATUS_OPTS[0];
    return <Badge className={`${opt.cls} border rounded-sm font-normal`}>{opt.label}</Badge>;
  };

  const newCount = leads.filter((l) => l.status === "new").length;

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#0c0c0c] flex items-center justify-center">
        <Loader2 className="text-gold animate-spin" size={32} />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard-page" className="min-h-screen bg-[#0c0c0c] grain-overlay">
      <header className="relative z-10 border-b border-stone-800 bg-[#0c0c0c]/85 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div>
            <div className="font-serif text-xl text-white">{BRAND.name}</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold">Dashboard Richieste</div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={fetchLeads} data-testid="admin-refresh" className="w-10 h-10 border border-stone-700 flex items-center justify-center text-stone-300 hover:text-gold hover:border-gold transition-colors">
              <RefreshCw size={16} />
            </button>
            <button onClick={doLogout} data-testid="admin-logout" className="flex items-center gap-2 border border-stone-700 text-stone-300 hover:text-gold hover:border-gold transition-colors px-4 py-2.5 text-sm uppercase tracking-widest">
              <LogOut size={16} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10">
          <div className="border border-stone-800 bg-[#111] p-6">
            <div className="text-stone-500 text-xs uppercase tracking-widest">Totale richieste</div>
            <div className="font-serif text-4xl text-white mt-2">{leads.length}</div>
          </div>
          <div className="border border-gold/40 bg-[#111] p-6">
            <div className="text-stone-500 text-xs uppercase tracking-widest">Nuove</div>
            <div className="font-serif text-4xl text-gold mt-2">{newCount}</div>
          </div>
          <div className="border border-stone-800 bg-[#111] p-6 col-span-2 md:col-span-1">
            <div className="text-stone-500 text-xs uppercase tracking-widest">Confermate</div>
            <div className="font-serif text-4xl text-white mt-2">{leads.filter((l) => l.status === "won").length}</div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="text-gold animate-spin" size={30} /></div>
        ) : leads.length === 0 ? (
          <div className="border border-stone-800 bg-[#111] py-20 flex flex-col items-center text-stone-500">
            <Inbox size={40} className="mb-4" />
            <p>Nessuna richiesta ricevuta al momento.</p>
          </div>
        ) : (
          <div className="border border-stone-800 bg-[#111] overflow-x-auto">
            <Table data-testid="leads-table">
              <TableHeader>
                <TableRow className="border-stone-800 hover:bg-transparent">
                  <TableHead className="text-stone-400 uppercase text-xs tracking-widest">Cliente</TableHead>
                  <TableHead className="text-stone-400 uppercase text-xs tracking-widest">Contatti</TableHead>
                  <TableHead className="text-stone-400 uppercase text-xs tracking-widest">Servizio</TableHead>
                  <TableHead className="text-stone-400 uppercase text-xs tracking-widest">Dettagli</TableHead>
                  <TableHead className="text-stone-400 uppercase text-xs tracking-widest">Stato</TableHead>
                  <TableHead className="text-stone-400 uppercase text-xs tracking-widest text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((l) => (
                  <TableRow key={l.id} data-testid={`lead-row-${l.id}`} className="border-stone-800 hover:bg-stone-900/40">
                    <TableCell className="align-top">
                      <div className="text-white font-medium">{l.name}</div>
                      <div className="text-xs text-stone-500 mt-1">{new Date(l.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}</div>
                    </TableCell>
                    <TableCell className="align-top text-sm">
                      <a href={`mailto:${l.email}`} className="text-stone-300 hover:text-gold block">{l.email}</a>
                      {l.phone && <a href={`tel:${l.phone}`} className="text-stone-500 hover:text-gold block mt-1">{l.phone}</a>}
                    </TableCell>
                    <TableCell className="align-top">
                      <span className="text-gold text-sm capitalize">{l.service_type}</span>
                    </TableCell>
                    <TableCell className="align-top text-sm text-stone-400 max-w-xs">
                      {l.event_date && <div>Data: {l.event_date}</div>}
                      {l.location && <div>Luogo: {l.location}</div>}
                      {l.message && <div className="mt-1 text-stone-500 line-clamp-3">{l.message}</div>}
                    </TableCell>
                    <TableCell className="align-top">
                      <Select value={l.status} onValueChange={(v) => changeStatus(l.id, v)}>
                        <SelectTrigger data-testid={`status-select-${l.id}`} className="w-[140px] bg-stone-900 border-stone-800 text-white h-9 rounded-sm">
                          <SelectValue>{badgeFor(l.status)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent className="bg-stone-900 border-stone-800 text-white">
                          {STATUS_OPTS.map((o) => (
                            <SelectItem key={o.value} value={o.value} className="focus:bg-stone-800 focus:text-gold">{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="align-top text-right">
                      <button onClick={() => removeLead(l.id)} data-testid={`delete-lead-${l.id}`} className="text-stone-500 hover:text-red-400 transition-colors p-2">
                        <Trash2 size={16} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
